import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PropertyProvider } from "@/context/PropertyContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/RegisterWithPlan";
import QuotePage from "@/pages/QuotePage";
import QuoteViewPage from "@/pages/QuoteViewPage";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import ReservationsPage from "@/pages/ReservationsPage";
import RatesPage from "@/pages/RatesPage";
import GuestsPage from "@/pages/GuestsPage";
import OTAConnectionsPage from "@/pages/OTAConnectionsPage";
import SettingsPage from "@/pages/SettingsPage";
import FacturacionConfigPage from "@/pages/FacturacionConfigPage";
import FacturacionDocsPage from "@/pages/FacturacionDocsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPropertyView from "@/pages/AdminPropertyView";
import CarteraClientesPage from "@/pages/CarteraClientesPage";
import CuentasPorCobrarPage from "@/pages/CuentasPorCobrarPage";
import InventoryPage from "@/pages/InventoryPage";
import POSPage from "@/pages/POSPage";
import HabitacionesPage from "@/pages/HabitacionesPage";
// Apartamentos Module Pages
import CalendarioUnificadoPage from "@/pages/CalendarioUnificadoPage";
import ApartamentosPage from "@/pages/ApartamentosPage";
// Informes & Simulador
import InformesPage from "@/pages/InformesPage";
import InformesOTAPage from "@/pages/InformesOTAPage";
import SimuladorPage from "@/pages/SimuladorPage";
import MultiCalendarioPage from "@/pages/MultiCalendarioPage";
import ChannexPage from "@/pages/ChannexPage";
import WhatsAppPage from "@/pages/WhatsAppPage";
import CatalogPage from "@/pages/CatalogPage";
import UserManagementPage from "@/pages/UserManagementPage";
import BodegaPage from "@/pages/BodegaPage";
import BookingEnginePage from "@/pages/BookingEnginePage";
import AuditoriasPage from "@/pages/AuditoriasPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/cotizar" element={<QuotePage />} />
        <Route path="/cotizacion/:quoteId" element={<QuoteViewPage />} />
        <Route path="/reservar/:code" element={<BookingEnginePage />} />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />

        {/* Protected Routes */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reservations"
          element={
            <ProtectedRoute>
              <ReservationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rates"
          element={
            <ProtectedRoute>
              <RatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guests"
          element={
            <ProtectedRoute>
              <GuestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ota-connections"
          element={
            <ProtectedRoute>
              <OTAConnectionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute>
              <InventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos"
          element={
            <ProtectedRoute>
              <POSPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/habitaciones"
          element={
            <ProtectedRoute>
              <HabitacionesPage />
            </ProtectedRoute>
          }
        />
        
        {/* Apartamentos Module Routes */}
        <Route
          path="/calendario-unificado"
          element={
            <ProtectedRoute>
              <CalendarioUnificadoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/apartamentos"
          element={
            <ProtectedRoute>
              <ApartamentosPage />
            </ProtectedRoute>
          }
        />

        {/* Informes & Simulador */}
        <Route
          path="/informes"
          element={
            <ProtectedRoute>
              <InformesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/informes/ingresos-ota"
          element={
            <ProtectedRoute>
              <InformesOTAPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulador-tarifas"
          element={
            <ProtectedRoute>
              <SimuladorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/multi-calendario"
          element={
            <ProtectedRoute>
              <MultiCalendarioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tarifas"
          element={
            <ProtectedRoute>
              <RatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/huespedes"
          element={
            <ProtectedRoute>
              <GuestsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/property/:propertyId/view"
          element={
            <ProtectedRoute>
              <AdminPropertyView />
            </ProtectedRoute>
          }
        />

        {/* Ajustes & Ingresos/Egresos redirects - prevent fallback to landing */}
        <Route path="/cartera-clientes" element={<ProtectedRoute><CarteraClientesPage /></ProtectedRoute>} />
        <Route path="/cuentas-por-cobrar" element={<ProtectedRoute><CuentasPorCobrarPage /></ProtectedRoute>} />
        <Route path="/channex" element={<ProtectedRoute><ChannexPage /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppPage /></ProtectedRoute>} />
        <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
        <Route path="/facturacion/remisiones" element={<ProtectedRoute><FacturacionDocsPage tipo="remision" /></ProtectedRoute>} />
        <Route path="/facturacion/facturas" element={<ProtectedRoute><FacturacionDocsPage tipo="factura" /></ProtectedRoute>} />
        <Route path="/facturacion/configuracion" element={<ProtectedRoute><FacturacionConfigPage /></ProtectedRoute>} />
        <Route path="/usuarios" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
        <Route path="/bodega" element={<ProtectedRoute><BodegaPage /></ProtectedRoute>} />
        <Route path="/auditorias" element={<ProtectedRoute><AuditoriasPage /></ProtectedRoute>} />
        <Route path="/ajustes/comisiones" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/ajustes/propiedad" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/ajustes/propiedades" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/ajustes/tarifas" element={<ProtectedRoute><RatesPage /></ProtectedRoute>} />
        <Route path="/ingresos-egresos" element={<ProtectedRoute><InformesPage /></ProtectedRoute>} />
        <Route path="/ingresos-egresos/ingreso" element={<ProtectedRoute><InformesPage /></ProtectedRoute>} />
        <Route path="/ingresos-egresos/gasto" element={<ProtectedRoute><InformesPage /></ProtectedRoute>} />
        <Route path="/ingresos-egresos/turnos" element={<ProtectedRoute><InformesPage /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PropertyProvider>
          <AppContent />
        </PropertyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
